<template>
  <div
    class="sidebar"
    :class="{
      'sidebar--collapsed': isCollapsed,
      'sidebar--mobile-open': isMobileMenuOpen,
    }"
  >
    <div class="sidebar__brand" v-if="!isCollapsed">
      <img src="@shared/assets/img/logo-wordmark.svg" alt="Funnelflow" class="sidebar__logo" />
    </div>

    <div class="sidebar__header" v-if="!isCollapsed">
      <div class="sidebar__organization">
        <div
          class="company-switcher"
          :class="{ 'company-switcher--static': !canSwitchOrganization }"
          @click="canSwitchOrganization && toggleOrganizationSwitcher()"
        >
          <div class="company-switcher__header">
            <div class="company-switcher__logo">
              <AvatarElement v-bind="organizationAvatar" />
            </div>
            <div class="company-switcher__name">
              {{ currentOrganization.name }}
              <small v-if="anonymousMode" class="company-switcher__anon-tag">Demo</small>
            </div>
            <div class="company-switcher__chevrons" v-if="canSwitchOrganization">
              <BaseIcon name="chevron" size="10" />
              <BaseIcon name="chevron" size="10" rotate="180" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="sidebar__org-mini"
      v-else
      :class="{ 'sidebar__org-mini--static': !canSwitchOrganization }"
      v-tippy="{ content: currentOrganization.name, placement: 'right' }"
      @click="canSwitchOrganization && toggleOrganizationSwitcher()"
    >
      <AvatarElement v-bind="organizationAvatar" />
    </div>

    <nav class="sidebar__nav">
      <!-- Installer Portal Navigation -->
      <div class="sidebar__section" v-if="isInstaller && type === 'default'">
        <h3 class="sidebar__section-title" v-if="!isCollapsed">Mijn werk</h3>
        <ul class="sidebar__nav-list">
          <li class="sidebar__nav-item">
            <router-link
              to="/vandaag"
              class="sidebar__nav-link"
              v-tippy="isCollapsed ? { content: 'Vandaag', placement: 'right' } : null"
            >
              <BaseIcon name="calendar" size="20" />
              <span class="sidebar__nav-text" v-if="!isCollapsed">Vandaag</span>
            </router-link>
          </li>
          <li class="sidebar__nav-item">
            <router-link
              to="/mijn-week"
              class="sidebar__nav-link"
              v-tippy="isCollapsed ? { content: 'Mijn week', placement: 'right' } : null"
            >
              <BaseIcon name="calendar" size="20" />
              <span class="sidebar__nav-text" v-if="!isCollapsed">Mijn week</span>
            </router-link>
          </li>
          <li class="sidebar__nav-item">
            <router-link
              to="/geschiedenis"
              class="sidebar__nav-link"
              v-tippy="isCollapsed ? { content: 'Geschiedenis', placement: 'right' } : null"
            >
              <BaseIcon name="clock" size="20" />
              <span class="sidebar__nav-text" v-if="!isCollapsed">Geschiedenis</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="sidebar__section" v-if="isInstaller && type === 'default'">
        <h3 class="sidebar__section-title" v-if="!isCollapsed">Klantinfo</h3>
        <ul class="sidebar__nav-list">
          <li class="sidebar__nav-item">
            <router-link
              to="/crm"
              class="sidebar__nav-link"
              v-tippy="isCollapsed ? { content: 'Klanten', placement: 'right' } : null"
            >
              <BaseIcon name="users" size="20" />
              <span class="sidebar__nav-text" v-if="!isCollapsed">Klanten</span>
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Home (standalone, no section heading) -->
      <div class="sidebar__section" v-if="type === 'default' && !isInstaller">
        <ul class="sidebar__nav-list">
          <li class="sidebar__nav-item">
            <router-link
              to="/home"
              class="sidebar__nav-link sidebar__nav-link--top"
              v-tippy="isCollapsed ? { content: $t('navigation.home'), placement: 'right' } : null"
            >
              <BaseIcon name="home" size="17" />
              <span class="sidebar__nav-text" v-if="!isCollapsed">
                {{ $t('navigation.home') }}
              </span>
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Grouped navigation (collapsible per group) -->
      <template v-if="type === 'default' && !isInstaller">
        <div
          class="sidebar__section sidebar__section--group"
          v-for="group in navGroups"
          :key="group.id"
        >
          <button
            v-if="!isCollapsed"
            class="sidebar__group-header"
            :class="{ 'sidebar__group-header--active': isGroupActive(group) }"
            type="button"
            :aria-expanded="isGroupOpen(group.id) ? 'true' : 'false'"
            @click="toggleGroup(group.id)"
          >
            <BaseIcon :name="group.icon" size="17" />
            <span class="sidebar__group-label">{{ group.label }}</span>
            <BaseIcon
              name="chevron"
              size="14"
              class="sidebar__group-chevron"
              :class="{ 'sidebar__group-chevron--open': isGroupOpen(group.id) }"
            />
          </button>
          <ul class="sidebar__nav-list" v-show="isCollapsed || isGroupOpen(group.id)">
            <li
              class="sidebar__nav-item"
              :class="{ 'sidebar__nav-item--disabled': item.disabled }"
              v-for="item in group.items"
              :key="item.to"
              v-tippy="
                item.disabled
                  ? 'Coming soon'
                  : isCollapsed
                    ? { content: item.label, placement: 'right' }
                    : null
              "
            >
              <component
                :is="item.disabled ? 'span' : 'router-link'"
                :to="item.disabled ? undefined : item.to"
                class="sidebar__nav-link"
                :class="{ 'sidebar__nav-link--disabled': item.disabled }"
              >
                <BaseIcon v-if="isCollapsed" :name="item.icon" size="20" />
                <span class="sidebar__nav-text" v-else>{{ item.label }}</span>
              </component>
            </li>
          </ul>
        </div>
      </template>
    </nav>

    <div class="sidebar__footer">
      <!-- Collapse + support sit directly above the account menu -->
      <div class="sidebar__section sidebar__section--utility">
        <ul class="sidebar__nav-list">
          <li class="sidebar__nav-item">
            <button
              type="button"
              class="sidebar__nav-link sidebar__nav-link--button sidebar__nav-link--top"
              v-tippy="
                isCollapsed
                  ? { content: $t('navigation.expand_sidebar'), placement: 'right' }
                  : null
              "
              @click="toggleSidebar"
            >
              <BaseIcon name="sidebar" size="17" />
              <span class="sidebar__nav-text" v-if="!isCollapsed">
                {{ $t('navigation.collapse_sidebar') }}
              </span>
            </button>
          </li>
          <li class="sidebar__nav-item" v-if="!isInstaller && chatwootAvailable">
            <button
              type="button"
              class="sidebar__nav-link sidebar__nav-link--button sidebar__nav-link--top"
              v-tippy="
                isCollapsed ? { content: $t('navigation.support'), placement: 'right' } : null
              "
              @click="openSupportChat"
            >
              <BaseIcon name="chat" size="17" />
              <span class="sidebar__nav-text" v-if="!isCollapsed">
                {{ $t('navigation.support') }}
              </span>
            </button>
          </li>
        </ul>
      </div>

      <div class="sidebar__account-section">
        <div class="account-dropdown" v-click-outside="closeAccountMenu">
          <button
            type="button"
            class="account-dropdown__trigger"
            :class="{ 'account-dropdown__trigger--open': showAccountMenu }"
            :aria-expanded="showAccountMenu ? 'true' : 'false'"
            v-tippy="
              isCollapsed
                ? {
                    content: userFullName,
                    placement: 'right',
                  }
                : null
            "
            @click="toggleAccountMenu"
          >
            <AvatarElement :title="userAvatar ? null : userInitial" :backgroundImage="userAvatar" />
            <div class="account-dropdown__info" v-if="!isCollapsed">
              <span class="account-dropdown__name">{{ userFullName }}</span>
              <span class="account-dropdown__role">{{ roleLabel }}</span>
            </div>
            <BaseIcon name="chevron" size="14" v-if="!isCollapsed" />
          </button>
          <transition name="account-menu">
            <div class="account-dropdown__menu" v-if="showAccountMenu">
              <div class="account-dropdown__head">
                <AvatarElement
                  :title="userAvatar ? null : userInitial"
                  :backgroundImage="userAvatar"
                />
                <div class="account-dropdown__head-info">
                  <span class="account-dropdown__name">{{ userFullName }}</span>
                  <span class="account-dropdown__email">{{ currentUser?.email }}</span>
                </div>
              </div>
              <div class="account-dropdown__group">
                <router-link
                  to="/settings/my-account"
                  class="account-dropdown__item"
                  @click="closeAccountMenu"
                >
                  <BaseIcon name="user" size="16" />
                  <span>{{ $t('navigation.my_account') }}</span>
                </router-link>
                <router-link
                  v-if="isAdmin"
                  to="/settings/organization"
                  class="account-dropdown__item"
                  @click="closeAccountMenu"
                >
                  <BaseIcon name="settings" size="16" />
                  <span>{{ $t('navigation.settings') }}</span>
                </router-link>
                <button
                  v-if="isSuperAdmin"
                  class="account-dropdown__item"
                  type="button"
                  role="switch"
                  :aria-checked="anonymousMode"
                  @click="toggleAnonymousMode"
                >
                  <BaseIcon name="eye" size="16" />
                  <span>{{ $t('navigation.anonymous_mode') }}</span>
                  <span class="sidebar__switch" :class="{ 'sidebar__switch--on': anonymousMode }">
                    <span class="sidebar__switch-dot" />
                  </span>
                </button>
                <router-link
                  v-if="isPlatformAdmin && !isInstaller"
                  to="/platform"
                  class="account-dropdown__item account-dropdown__item--platform"
                  @click="closeAccountMenu"
                >
                  <BaseIcon name="exclamation-triangle" size="16" />
                  <span>{{ $t('platform.sidebar.link') }}</span>
                </router-link>
              </div>
              <div class="account-dropdown__divider"></div>
              <button
                class="account-dropdown__item account-dropdown__item--danger"
                type="button"
                @click="logOut"
              >
                <BaseIcon name="arrow-right" size="16" rotate="180" />
                <span>{{ $t('navigation.sign_out') }}</span>
              </button>
            </div>
          </transition>
        </div>
      </div>

      <button class="sidebar__mobile-toggle lg-down" @click="toggleMobileMenu">
        <BaseIcon name="hamburger" size="20" />
      </button>
    </div>
  </div>
  <!-- Mobile Bottom Toolbar — Installer (real or simulated) -->
  <nav class="mobile-toolbar" v-if="type === 'default' && showAsInstaller">
    <router-link to="/vandaag" class="mobile-toolbar__item">
      <BaseIcon name="calendar" size="20" />
      <span class="mobile-toolbar__label">Vandaag</span>
    </router-link>
    <router-link to="/mijn-week" class="mobile-toolbar__item">
      <BaseIcon name="calendar" size="20" />
      <span class="mobile-toolbar__label">Week</span>
    </router-link>
    <router-link to="/geschiedenis" class="mobile-toolbar__item">
      <BaseIcon name="clock" size="20" />
      <span class="mobile-toolbar__label">Historie</span>
    </router-link>
    <button class="mobile-toolbar__item" @click="showMoreDrawer = true">
      <BaseIcon name="user" size="20" />
      <span class="mobile-toolbar__label">Profiel</span>
    </button>
  </nav>

  <!-- Mobile Bottom Toolbar — Default (admin/owner) -->
  <nav class="mobile-toolbar" v-else-if="type === 'default' && !showAsInstaller">
    <router-link to="/home" class="mobile-toolbar__item">
      <BaseIcon name="home" size="20" />
      <span class="mobile-toolbar__label">{{ $t('navigation.home') }}</span>
    </router-link>
    <router-link to="/deals" class="mobile-toolbar__item">
      <BaseIcon name="handshake" size="20" />
      <span class="mobile-toolbar__label">{{ $t('navigation.deals') }}</span>
    </router-link>
    <router-link to="/proposals" class="mobile-toolbar__item">
      <BaseIcon name="file-text" size="20" />
      <span class="mobile-toolbar__label">{{ $t('navigation.proposals') }}</span>
    </router-link>
    <router-link to="/stats" class="mobile-toolbar__item">
      <BaseIcon name="pie-chart" size="20" />
      <span class="mobile-toolbar__label">{{ $t('navigation.stats') }}</span>
    </router-link>
    <router-link to="/appointments" class="mobile-toolbar__item">
      <BaseIcon name="calendar" size="20" />
      <span class="mobile-toolbar__label">{{ $t('navigation.appointments') }}</span>
    </router-link>
    <button class="mobile-toolbar__item" @click="showMoreDrawer = true">
      <BaseIcon name="dots-horizontal" size="20" />
      <span class="mobile-toolbar__label">{{ $t('navigation.more') }}</span>
    </button>
  </nav>

  <!-- More Drawer -->
  <BottomDrawer v-model="showMoreDrawer">
    <div class="more-drawer">
      <!-- Admin/Installer View Toggle -->
      <div v-if="canToggleView" class="more-drawer__view-toggle">
        <button
          class="more-drawer__toggle-btn"
          :class="{ 'more-drawer__toggle-btn--active': !mobileInstallerMode }"
          @click="setMobileViewMode(false)"
        >
          <BaseIcon name="settings" size="16" />
          Admin
        </button>
        <button
          class="more-drawer__toggle-btn"
          :class="{ 'more-drawer__toggle-btn--active': mobileInstallerMode }"
          @click="setMobileViewMode(true)"
        >
          <BaseIcon name="wrench" size="16" />
          Monteur
        </button>
      </div>
      <div v-if="canToggleView" class="more-drawer__divider"></div>

      <!-- Organization Switcher -->
      <button class="more-drawer__org" @click="handleMoreOrgSwitch">
        <AvatarElement
          :backgroundImage="currentOrganization?.brand_settings?.icon"
          v-bind="
            !currentOrganization?.brand_settings?.icon && {
              title: currentOrganization?.name
                ? currentOrganization.name.substring(0, 2).toUpperCase()
                : '',
              color: currentOrganization?.brand_settings?.primary_color,
              textColor: currentOrganization?.brand_settings?.primary_accent_color,
            }
          "
          size="sm"
        />
        <div class="more-drawer__org-info">
          <span class="more-drawer__org-name">
            {{ currentOrganization?.name }}
            <small v-if="anonymousMode">Demo</small>
          </span>
          <span class="more-drawer__org-switch">{{ $t('navigation.switch_organization') }}</span>
        </div>
        <BaseIcon name="chevron" size="12" />
      </button>

      <template v-if="isSuperAdmin">
        <div class="more-drawer__divider"></div>

        <button
          class="more-drawer__item"
          type="button"
          role="switch"
          :aria-checked="anonymousMode"
          @click="toggleAnonymousMode"
        >
          <BaseIcon name="user" size="18" />
          <span>{{ $t('navigation.anonymous_mode') }}</span>
          <span
            class="sidebar__switch more-drawer__switch"
            :class="{ 'sidebar__switch--on': anonymousMode }"
          >
            <span class="sidebar__switch-dot" />
          </span>
        </button>
      </template>

      <div class="more-drawer__divider"></div>

      <!-- Navigation Items -->
      <button
        v-if="!isInstaller"
        class="more-drawer__item"
        @click="handleMoreNavigate('/submissions')"
      >
        <BaseIcon name="inbox" size="18" />
        <span>{{ $t('navigation.requests') }}</span>
      </button>
      <button
        v-if="hasSlimWerken && isAdmin"
        class="more-drawer__item"
        @click="handleMoreNavigate('/projects')"
      >
        <BaseIcon name="clipboard" size="18" />
        <span>{{ $t('navigation.projects') }}</span>
      </button>
      <button
        v-if="hasSlimInnen && isAdmin"
        class="more-drawer__item"
        @click="handleMoreNavigate('/invoices')"
      >
        <BaseIcon name="euro" size="18" />
        <span>{{ $t('navigation.invoices') }}</span>
      </button>
      <button class="more-drawer__item" @click="handleMoreNavigate('/crm')">
        <BaseIcon name="users" size="18" />
        <span>{{ $t('navigation.crm') }}</span>
      </button>

      <div class="more-drawer__divider"></div>

      <a
        href="https://funnelflow-docs.onrender.com/"
        target="_blank"
        rel="noopener"
        class="more-drawer__item"
        @click="showMoreDrawer = false"
      >
        <BaseIcon name="question" size="18" />
        <span>{{ $t('navigation.documentation') }}</span>
      </a>
      <button v-if="chatwootAvailable" class="more-drawer__item" @click="handleMoreSupport">
        <BaseIcon name="chat" size="18" />
        <span>{{ $t('navigation.support') }}</span>
      </button>

      <div class="more-drawer__divider"></div>

      <button class="more-drawer__item more-drawer__item--danger" @click="handleMoreLogout">
        <BaseIcon name="arrow-right" size="18" rotate="180" />
        <span>{{ $t('navigation.sign_out') }}</span>
      </button>
    </div>
  </BottomDrawer>
</template>

<script>
  import { computed, ref, watch, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { useUserStore } from '@/store/userStore';
  import AvatarElement from '@/components/partials/Avatar.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import BottomDrawer from '@/components/partials/BottomDrawer.vue';
  import { useUIStore } from '@/store/uiStore';
  import { useAnonymousStore } from '@/store/anonymousStore';
  import OrganizationSwitcherModal from '@/components/organizationSwitch/OrganizationSwitcherModal.vue';
  import { useI18n } from '@/composables/useI18n';

  import { useRoleCheck } from '@/composables/useRoleCheck';
  import { useModules } from '@/composables/useModules';
  import { openChatwootChat, isChatwootEnabled } from '@/plugins/chatwoot';

  // Check if we're in development mode
  const isDevMode = import.meta.env.DEV;

  const OPEN_GROUPS_KEY = 'sidebar_open_groups';
  const COLLAPSED_KEY = 'sidebar_collapsed';

  const readPreference = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch (error) {
      return null;
    }
  };

  const writePreference = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage unavailable (private mode) — preference stays in memory only
    }
  };

  export default {
    name: 'SidebarNav',
    components: {
      AvatarElement,
      BaseIcon,
      BottomDrawer,
      OrganizationSwitcherModal,
    },
    props: {
      type: {
        type: String,
        default: 'default',
      },
    },
    setup() {
      const userStore = useUserStore();
      const router = useRouter();
      const { isAdmin, isMember, isSuperAdmin, isInstaller, isPlatformAdmin } = useRoleCheck();
      const { t } = useI18n();
      const currentOrganization = computed(() => userStore.currentOrganization);
      const canSwitchOrganization = computed(() => (userStore.organizations?.length || 0) > 1);
      const organizationAvatar = computed(() => {
        const brand = currentOrganization.value?.brand_settings;
        if (brand?.icon) return { backgroundImage: brand.icon };
        return {
          title: currentOrganization.value?.name?.substring(0, 2).toUpperCase() || '',
          color: brand?.primary_color,
          textColor: brand?.primary_accent_color,
        };
      });
      const currentUser = computed(() => userStore.user);
      const { hasSlimWerken, hasSlimInnen } = useModules();
      const userInitial = computed(() => {
        if (userStore.user && userStore.user.user_metadata.first_name) {
          return (
            userStore.user.user_metadata.first_name.charAt(0) +
            userStore.user.user_metadata.last_name.charAt(0)
          );
        }
        return '';
      });

      const userFullName = computed(() => {
        const meta = userStore.user?.user_metadata || {};
        const name = [meta.first_name, meta.last_name].filter(Boolean).join(' ').trim();
        return name || userStore.user?.email || t('navigation.account');
      });

      const roleLabel = computed(() => {
        const role = userStore.currentOrganization?.role;
        if (role === 'super_admin') return 'Super Admin';
        if (role === 'owner') return t('navigation.owner');
        if (role === 'admin') return t('navigation.admin');
        if (role === 'installer') return 'Monteur';
        return t('navigation.member');
      });

      const isMobileMenuOpen = ref(false);
      const isCollapsed = ref(readPreference(COLLAPSED_KEY) === true);
      const showMoreDrawer = ref(false);
      const uiStore = useUIStore();
      const anonymousStore = useAnonymousStore();
      const anonymousMode = computed(() => anonymousStore.enabled);
      const mobileInstallerMode = computed(() => uiStore.mobileInstallerMode);

      const showAsInstaller = computed(() => isInstaller.value || mobileInstallerMode.value);

      const canToggleView = computed(
        () => (isAdmin.value || isSuperAdmin.value) && !isInstaller.value
      );

      const setMobileViewMode = (installerMode) => {
        uiStore.setMobileInstallerMode(installerMode);
        showMoreDrawer.value = false;
        // Bij wisselen naar Monteur direct naar /vandaag navigeren
        if (installerMode && router.currentRoute.value?.path !== '/vandaag') {
          router.push('/vandaag');
        }
      };

      const route = router.currentRoute;
      watch(
        () => route.value?.path,
        (path) => {
          if (path === '/planning') {
            isCollapsed.value = true;
          }
        },
        { immediate: true }
      );

      const navGroups = computed(() => {
        const groups = [
          {
            id: 'crm',
            label: t('navigation.group_crm'),
            icon: 'crm',
            items: [
              { label: t('navigation.deals'), to: '/deals', icon: 'handshake' },
              { label: t('navigation.crm'), to: '/crm', icon: 'users' },
            ],
          },
          {
            id: 'sales',
            label: t('navigation.group_sales'),
            icon: 'handshake',
            items: [
              { label: t('navigation.proposals'), to: '/proposals', icon: 'document' },
              { label: t('navigation.appointments'), to: '/appointments', icon: 'calendar' },
              { label: t('navigation.submissions'), to: '/submissions', icon: 'inbox' },
            ],
          },
          {
            id: 'operations',
            label: t('navigation.group_operations'),
            icon: 'clipboard',
            items: [
              {
                label: t('navigation.projects'),
                to: '/projects',
                icon: 'clipboard',
                disabled: !hasSlimWerken.value,
              },
              {
                label: t('navigation.orders'),
                to: '/orders',
                icon: 'package',
                disabled: !hasSlimWerken.value,
              },
              {
                label: t('navigation.planning'),
                to: '/planning',
                icon: 'calendar-clock',
                disabled: !hasSlimWerken.value,
              },
              {
                label: t('navigation.service'),
                to: '/servicemeldingen',
                icon: 'wrench',
                disabled: !hasSlimWerken.value,
              },
            ],
          },
          {
            id: 'finance',
            label: t('navigation.group_finance'),
            icon: 'finance',
            items: [
              {
                label: t('navigation.invoices'),
                to: '/invoices',
                icon: 'euro',
                visible: hasSlimInnen.value || isSuperAdmin.value,
              },
              { label: t('stats_dashboard.nav_label'), to: '/stats', icon: 'pie-chart' },
            ],
          },
          {
            id: 'setup',
            label: t('navigation.group_setup'),
            icon: 'sliders',
            items: [
              { label: t('navigation.all_funnels'), to: '/all-flows', icon: 'target' },
              {
                label: t('navigation.products'),
                to: '/products',
                icon: 'box',
                visible: isAdmin.value,
              },
              {
                label: t('navigation.catalog'),
                to: '/products/catalog',
                icon: 'cubes',
                visible: isSuperAdmin.value,
              },
              {
                label: t('navigation.discounts'),
                to: '/discounts',
                icon: 'calculator',
                visible: isAdmin.value,
              },
              {
                label: t('navigation.automations'),
                to: '/automations',
                icon: 'zap',
                visible: isAdmin.value,
              },
            ],
          },
        ];

        return groups
          .map((group) => ({
            ...group,
            items: group.items.filter((item) => item.visible !== false),
          }))
          .filter((group) => group.items.length > 0);
      });

      const storedGroups = readPreference(OPEN_GROUPS_KEY);
      const openGroups = ref(
        Array.isArray(storedGroups) ? storedGroups : navGroups.value.map((group) => group.id)
      );

      const isGroupOpen = (groupId) => openGroups.value.includes(groupId);

      const isGroupActive = (group) =>
        group.items.some((item) => route.value?.path?.startsWith(item.to));

      const toggleGroup = (groupId) => {
        openGroups.value = isGroupOpen(groupId)
          ? openGroups.value.filter((id) => id !== groupId)
          : [...openGroups.value, groupId];
        writePreference(OPEN_GROUPS_KEY, openGroups.value);
      };

      const userAvatar = computed(() => userStore.user.user_metadata.avatar_url);

      function toggleOrganizationSwitcher() {
        const modalId = uiStore.addModal(OrganizationSwitcherModal, {
          title: t('navigation.choose_organization'),
          description: t('navigation.choose_organization_description'),
          size: 'xl',
          onSubmit: async (data) => {
            uiStore.closeModal(modalId);
          },
          onError: (errors) => {
            console.error('Form submission errors:', errors);
          },
        });
      }

      async function logOut() {
        try {
          await userStore.logout();
          router.push('/login');
        } catch (error) {
          console.error('Error signing out: ', error);
        }
      }

      const showAccountMenu = ref(false);

      function toggleAccountMenu() {
        showAccountMenu.value = !showAccountMenu.value;
      }

      function closeAccountMenu() {
        showAccountMenu.value = false;
      }

      function toggleMobileMenu() {
        isMobileMenuOpen.value = !isMobileMenuOpen.value;
      }

      // Only an explicit toggle is a preference — the /planning route collapse is not
      function toggleSidebar() {
        isCollapsed.value = !isCollapsed.value;
        writePreference(COLLAPSED_KEY, isCollapsed.value);
      }

      onMounted(() => {
        document.addEventListener('click', (e) => {
          if (
            !e.target.closest('.sidebar__nav-list--main') &&
            !e.target.closest('.sidebar__hamburger')
          ) {
            isMobileMenuOpen.value = false;
          }
        });
      });
      const handleMoreNavigate = (path) => {
        showMoreDrawer.value = false;
        router.push(path);
      };

      const handleMoreLogout = () => {
        showMoreDrawer.value = false;
        logOut();
      };

      const handleMoreOrgSwitch = () => {
        showMoreDrawer.value = false;
        toggleOrganizationSwitcher();
      };

      const chatwootAvailable = computed(() => isChatwootEnabled());

      const toggleAnonymousMode = () => {
        anonymousStore.toggle();
      };

      const handleMoreSupport = () => {
        showMoreDrawer.value = false;
        openChatwootChat();
      };

      const openSupportChat = () => {
        openChatwootChat();
      };

      return {
        showAccountMenu,
        toggleAccountMenu,
        closeAccountMenu,
        userInitial,
        userFullName,
        roleLabel,
        navGroups,
        isGroupOpen,
        isGroupActive,
        toggleGroup,
        openSupportChat,
        logOut,
        currentOrganization,
        canSwitchOrganization,
        organizationAvatar,
        currentUser,
        toggleOrganizationSwitcher,
        userAvatar,
        isAdmin,
        isMember,
        isSuperAdmin,
        isInstaller,
        isPlatformAdmin,
        isMobileMenuOpen,
        toggleMobileMenu,
        isCollapsed,
        toggleSidebar,
        isDevMode,
        hasSlimWerken,
        hasSlimInnen,
        showMoreDrawer,
        handleMoreNavigate,
        handleMoreLogout,
        handleMoreOrgSwitch,
        handleMoreSupport,
        chatwootAvailable,
        mobileInstallerMode,
        showAsInstaller,
        canToggleView,
        setMobileViewMode,
        anonymousMode,
        toggleAnonymousMode,
        $t: t,
      };
    },
  };
</script>

<style lang="scss" scoped>
  .sidebar {
    width: 100%;
    max-width: 260px;
    min-width: 260px;
    height: calc(100vh - var(--app-alert-offset, 0px));
    display: flex;
    flex-direction: column;
    position: fixed;
    left: 0;
    top: var(--app-alert-offset, 0px);
    z-index: 1000;
    transition: all 0.3s ease;
    background-color: var(--slate-50);

    &--collapsed {
      width: 72px;
      max-width: 72px;
      min-width: 72px;

      .sidebar__section {
        padding: 0 0.5rem;
      }

      .sidebar__nav-link {
        justify-content: center;
        padding: 0.5rem;
      }

      .sidebar__section--group {
        margin-bottom: 0.75rem;

        .sidebar__nav-list {
          margin-left: 0;
          padding-left: 0;
          border-left: 0;
        }
      }
    }

    &__brand {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1rem 0.75rem;
    }

    &__logo {
      display: block;
      height: 32px;
      width: auto;
      max-width: 100%;
      object-fit: contain;
    }

    &__header {
      padding: 0 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__organization {
      flex: 1;
      overflow: hidden;
    }

    &__org-mini {
      display: flex;
      justify-content: center;
      padding: 0.5rem;
      cursor: pointer;

      &--static {
        cursor: default;
      }
    }

    &__nav {
      flex: 1;
      padding: 1rem 0 0 0;
      overflow-y: auto;
    }

    &__section {
      margin-bottom: 1rem;
      padding: 0 0.5rem;

      &--group {
        margin-bottom: 0.5rem;
      }

      &--utility {
        margin-bottom: 0;
      }
    }

    &__nav &__section:last-child {
      margin-bottom: 0;
    }

    &__group-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      padding: 0.375rem 0.375rem;
      border: 0;
      border-radius: 0.5rem;
      background: transparent;
      cursor: pointer;
      color: var(--slate-500);
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      text-align: left;
      transition: color 0.2s ease;

      .icon {
        flex-shrink: 0;
        color: var(--slate-400);
      }

      @include hover {
        color: var(--slate-700);

        .icon {
          color: var(--slate-600);
        }
      }

      &:focus-visible {
        outline: 2px solid var(--brand-500);
        outline-offset: 2px;
      }

      &--active {
        color: var(--slate-800);

        .icon {
          color: var(--slate-700);
        }
      }
    }

    &__group-label {
      flex: 1;
      @include ellipsis(1);
    }

    &__group-chevron {
      transform: rotate(180deg);
      transition: transform 0.2s ease;

      &--open {
        transform: rotate(0deg);
      }
    }

    &__section-title {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--slate-400);
      margin: 0 0 0.75rem 0;
      padding: 0 0.5rem;
    }

    &__nav-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;

      .sidebar__section--group & {
        margin-left: 0.5rem;
        padding-left: 0.75rem;
        border-left: 1px solid var(--slate-200);
      }

      @include lg-down {
        &--open {
          display: flex;
        }
      }
    }

    &__nav-item {
      &--automations {
        @include lg-down {
          display: none;
        }
      }
      &--disabled {
        opacity: 0.4;
        .sidebar__nav-link {
          cursor: not-allowed;
          pointer-events: none;
        }
      }
    }

    &__nav-link {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      padding: 0.375rem 0.5rem;
      border-radius: 0.5rem;
      border: 1px solid transparent;
      color: var(--slate-600);
      text-decoration: none;
      font-weight: 500;
      font-size: 14px;
      transition: all 0.2s ease;

      &--disabled {
        opacity: 0.35;
        cursor: not-allowed;
        pointer-events: none;
      }

      &--button {
        background: transparent;
        cursor: pointer;
        text-align: left;
        font-family: inherit;
      }

      // Top-level entries match the group headers instead of the nested items
      &--top {
        padding: 0.375rem;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--slate-500);

        .icon {
          color: var(--slate-400);
        }

        @include hover {
          .icon {
            color: var(--slate-600);
          }
        }

        &.router-link-active .icon {
          color: var(--slate-700);
        }
      }

      @include hover {
        background-color: rgba(255, 255, 255, 0.7);
        color: var(--slate-900);
      }

      &.router-link-active {
        background-color: var(--white);
        border-color: var(--slate-200);
        color: var(--slate-900);
        font-weight: 600;
        box-shadow: 0 1px 2px rgba(55, 65, 81, 0.08);
      }

      .icon {
        flex-shrink: 0;
      }
    }

    &__nav-text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: opacity 0.3s ease;
    }

    &__footer {
      padding: 0.5rem 0 1rem 0;
      margin-top: auto;
    }

    &__account-section {
      padding: 0.25rem 0.5rem 0 0.5rem;
    }

    &__switch {
      position: relative;
      flex-shrink: 0;
      width: 2.25rem;
      height: 1.3125rem;
      border-radius: 9999px;
      background: var(--slate-200);
      transition: background-color 0.15s ease-out;

      &--on {
        background: var(--brand-500);
      }
    }

    &__switch-dot {
      position: absolute;
      top: 50%;
      left: 4px;
      width: 14px;
      height: 14px;
      border-radius: 9999px;
      background: var(--white);
      transform: translateY(-50%);
      transition: left 0.15s ease-out;

      .sidebar__switch--on & {
        left: 18px;
      }
    }

    &__mobile-toggle {
      display: none;
      background: none;
      border: none;
      padding: 0.5rem;
      color: var(--slate-500);
      cursor: pointer;
      border-radius: 0.5rem;

      @include lg-down {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      @include hover {
        background-color: var(--slate-50);
        color: var(--slate-700);
      }
    }

    // Mobile responsive - hide sidebar on lg-down
    @include lg-down {
      display: none;
    }
  }

  // Mobile Bottom Toolbar
  .mobile-toolbar {
    display: none;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: calc(64px + env(safe-area-inset-bottom));
    background-color: #ffffff;
    border-top: 1px solid var(--slate-200);
    z-index: 1000;
    padding: 0 0.5rem;
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);

    @include lg-down {
      display: flex;
      align-items: center;
      justify-content: space-around;
    }

    &__item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.25rem;
      padding: 0.5rem;
      border-radius: 0.5rem;
      color: var(--slate-500);
      text-decoration: none;
      background: none;
      border: 1px solid transparent;
      cursor: pointer;
      transition: all 0.2s ease;
      min-width: 56px;

      &.router-link-active {
        color: var(--brand-500);
        background: var(--slate-50);
        border-color: var(--slate-200);

        .mobile-toolbar__label {
          font-weight: 600;
        }
      }

      @include hover {
        color: var(--slate-700);
        background-color: var(--slate-50);
      }
    }

    &__label {
      font-size: 10px;
      font-weight: 500;
      white-space: nowrap;
    }
  }

  .more-drawer {
    padding: 0.5rem 0;

    &__view-toggle {
      display: flex;
      gap: 0.25rem;
      margin: 0.25rem 1.25rem 0.5rem;
      padding: 0.25rem;
      background: var(--slate-100);
      border-radius: 0.5rem;
    }

    &__toggle-btn {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      border: none;
      border-radius: 0.375rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-500);
      background: transparent;
      cursor: pointer;
      transition: all 0.2s ease;

      .icon {
        color: var(--slate-400);
      }

      &--active {
        background: var(--white, #fff);
        color: var(--slate-900);
        box-shadow:
          0 1px 2px rgba(0, 0, 0, 0.06),
          0 1px 3px rgba(0, 0, 0, 0.1);

        .icon {
          color: var(--brand-600);
        }
      }
    }

    &__org {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
      padding: 0.75rem 1.25rem;
      background: none;
      border: none;
      cursor: pointer;
      text-align: left;
      transition: background-color 0.15s ease;

      &:active {
        background: var(--slate-50);
      }
    }

    &__org-info {
      flex: 1;
      min-width: 0;
    }

    &__org-name {
      display: flex;
      align-items: baseline;
      gap: 0.35rem;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-900);
      line-height: 1.3;

      small {
        font-size: 0.625rem;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--slate-500);
      }
    }

    &__org-switch {
      display: block;
      font-size: 0.75rem;
      color: var(--brand-600);
      font-weight: 500;
    }

    &__divider {
      height: 1px;
      background: var(--slate-100);
      margin: 0.25rem 1.25rem;
    }

    &__item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
      padding: 0.875rem 1.25rem;
      background: none;
      border: none;
      font-size: 0.9375rem;
      font-weight: 500;
      color: var(--slate-700);
      cursor: pointer;
      text-align: left;
      transition: background-color 0.15s ease;

      &:active {
        background: var(--slate-50);
      }

      .icon {
        color: var(--slate-400);
      }

      .more-drawer__switch {
        margin-left: auto;
      }

      &--danger {
        color: var(--red-600);

        .icon {
          color: var(--red-500);
        }
      }
    }
  }

  .company-switcher {
    display: flex;
    align-items: center;
    font-weight: 600;
    padding: 0.5rem;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 100%;
    background-color: #ffffff;

    @include hover {
      border-color: #d1d5db;
      background-color: #f9fafb;

      .company-switcher__chevrons {
        color: #6b7280;
      }
    }

    // Only one organization: show it, don't suggest it is clickable
    // (plain :hover here, the hover mixin forces cursor: pointer)
    &--static {
      cursor: default;

      &:hover {
        cursor: default;
        border-color: #e5e7eb;
        background-color: #ffffff;
      }
    }

    &__header {
      display: flex;
      align-items: center;
      gap: 0.875rem;
      width: 100%;
      overflow: hidden;
    }

    &__logo {
      flex-shrink: 0;
    }

    &__name {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 0.35rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 14px;
      font-weight: 600;
      color: #111827;
    }

    &__chevrons {
      display: flex;
      flex-direction: column;
      color: #9ca3af;
      flex-shrink: 0;
      transition: color 0.2s ease;
    }

    &__anon-tag {
      flex-shrink: 0;
      font-size: 0.625rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--slate-500);
    }
  }

  .account-dropdown {
    position: relative;

    &__trigger {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem;
      border-radius: 0.5rem;
      border: 1px solid transparent;
      background-color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      font-weight: 500;
      font-family: inherit;
      transition: all 0.2s ease;
      width: 100%;
      text-align: left;

      @include hover {
        background-color: var(--white);
        border-color: var(--slate-200);
      }

      &--open {
        background-color: var(--white);
        border-color: var(--slate-200);

        .icon:last-child {
          transform: rotate(180deg);
        }
      }

      .icon {
        color: var(--slate-500);
        flex-shrink: 0;
        transition: transform 0.2s ease;
      }
    }

    &__info {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      overflow: hidden;
    }

    &__name {
      font-size: 14px;
      font-weight: 600;
      color: var(--slate-900);
      width: 100%;
      @include ellipsis(1);
    }

    &__role {
      font-size: 12px;
      color: var(--slate-500);
      width: 100%;
      @include ellipsis(1);
    }

    &__email {
      font-size: 12px;
      color: var(--slate-500);
      width: 100%;
      @include ellipsis(1);
    }

    &__menu {
      position: absolute;
      bottom: calc(100% + 0.5rem);
      left: 0;
      width: 240px;
      background-color: var(--white);
      border: 1px solid var(--slate-200);
      border-radius: 0.75rem;
      padding: 0.375rem;
      box-shadow:
        0 10px 15px -3px rgb(0 0 0 / 0.1),
        0 4px 6px -2px rgb(0 0 0 / 0.05);
      z-index: 1001;
    }

    &__head {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem;
      overflow: hidden;
    }

    &__head-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
    }

    &__group {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding-top: 0.25rem;
      border-top: 1px solid var(--slate-100);
    }

    &__divider {
      height: 1px;
      background-color: var(--slate-100);
      margin: 0.25rem 0;
    }

    &__item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem;
      width: 100%;
      text-align: left;
      color: var(--slate-700);
      border: 0;
      background: transparent;
      border-radius: 0.5rem;
      font-weight: 500;
      font-family: inherit;
      text-decoration: none;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 14px;

      .icon {
        flex-shrink: 0;
        color: var(--slate-400);
        stroke: currentColor;
        fill: transparent;
      }

      .sidebar__switch {
        margin-left: auto;
      }

      @include hover {
        background-color: var(--slate-50);
        color: var(--slate-900);
      }

      &--danger {
        color: var(--red-600);

        .icon {
          color: var(--red-500);
        }

        @include hover {
          background-color: var(--red-50);
          color: var(--red-700);
        }
      }

      &--platform {
        color: var(--red-700);

        .icon {
          color: var(--red-600);
        }

        @include hover {
          background-color: var(--red-50);
          color: var(--red-700);
        }
      }
    }
  }

  .account-menu-enter-active,
  .account-menu-leave-active {
    transition:
      opacity 0.15s ease,
      transform 0.15s ease;
  }

  .account-menu-enter-from,
  .account-menu-leave-to {
    opacity: 0;
    transform: translateY(0.25rem);
  }
</style>
